import { useCallback, useEffect, useMemo, useState } from "react";
import { Building2, Layers, Sparkles, Trash2, Users, X } from "lucide-react";
import BONavigation from "../../../components/admin/BONavigation";
import {
    AnalyzedEstablishment,
    ClientCluster,
    ClientTypingQuery,
    ClientTypingRun,
    ClientTypingRunDetail,
    ClientTypingStats,
    ClientTypingUserStat,
    createClientCluster,
    deleteClientCluster,
    fetchAnalyzedEstablishments,
    fetchClientClusters,
    fetchClientTypingRun,
    fetchClientTypingRuns,
    fetchClientTypingStats,
    updateClientCluster,
} from "../../../interfaces/clientTypings";

/**
 * Everything the client-typing page produced: who analysed which clients, the profiles that
 * came back, and the clusters an admin builds out of them.
 *
 * Same division of labour as `/bo/ai-prompts`. The runs are recorded automatically and are
 * read-only here; the clusters are editorial — what counts as "the same kind of client" is a
 * judgement call, so the page's job is to make a profile easy to turn into a cluster, not to
 * guess the grouping. A client can sit in several clusters at once, and each one counts it.
 */

type Tab = "runs" | "establishments" | "clusters";

const PERIODS: { label: string; value: ClientTypingQuery["days"] }[] = [
    { label: "7 j", value: 7 },
    { label: "30 j", value: 30 },
    { label: "90 j", value: 90 },
    { label: "Tout", value: "all" },
];

function formatDate(value?: string | null) {
    if (!value) return "—";
    const date = new Date(value);
    const diffMins = Math.floor((Date.now() - date.getTime()) / 60000);
    if (diffMins < 1) return "À l'instant";
    if (diffMins < 60) return `Il y a ${diffMins}min`;
    if (diffMins < 1440) return `Il y a ${Math.floor(diffMins / 60)}h`;
    if (diffMins < 10080) return `Il y a ${Math.floor(diffMins / 1440)}j`;
    return date.toLocaleDateString("fr-FR", { day: "numeric", month: "short", year: "2-digit" });
}

// ─── Cluster assignment bar ───────────────────────────────────────────────────

function ClusterBar({
    label,
    ids,
    clusters,
    busy,
    origin,
    onDone,
    onClear,
}: {
    label: string;
    ids: string[];
    clusters: ClientCluster[];
    busy: boolean;
    origin?: string;
    onDone: (run: () => Promise<any>) => void;
    onClear: () => void;
}) {
    const [name, setName] = useState("");
    const [target, setTarget] = useState("");

    if (ids.length === 0) return null;

    return (
        <div className="bg-white border border-gray-900 rounded-xl p-4 mb-4 flex flex-wrap items-center gap-3">
            <span className="text-xs font-medium text-gray-900">{label}</span>
            <button onClick={onClear} className="text-xs text-gray-500 hover:text-gray-800">
                Désélectionner
            </button>

            <div className="flex items-center gap-2 ml-auto">
                <input
                    value={name}
                    onChange={e => setName(e.target.value)}
                    placeholder="Nom du nouveau cluster"
                    className="w-56 border border-gray-200 rounded-lg px-3 py-1.5 text-xs focus:outline-none focus:border-gray-400"
                />
                <button
                    disabled={busy || !name.trim()}
                    onClick={() => {
                        onDone(() =>
                            createClientCluster({ name: name.trim(), establishmentIds: ids, origin })
                        );
                        setName("");
                    }}
                    className="px-3 py-1.5 rounded-lg bg-[#1A1A1A] text-white text-xs font-medium disabled:opacity-40"
                >
                    Créer le cluster
                </button>
            </div>

            {clusters.length > 0 && (
                <div className="flex items-center gap-2">
                    <select
                        value={target}
                        onChange={e => setTarget(e.target.value)}
                        className="border border-gray-200 rounded-lg px-2 py-1.5 text-xs focus:outline-none focus:border-gray-400"
                    >
                        <option value="">…ou ajouter à un cluster</option>
                        {clusters.map(c => (
                            <option key={c._id} value={c._id}>
                                {c.name}
                            </option>
                        ))}
                    </select>
                    <button
                        disabled={busy || !target}
                        onClick={() => {
                            onDone(() => updateClientCluster(target, { addEstablishments: ids }));
                            setTarget("");
                        }}
                        className="px-3 py-1.5 rounded-lg border border-gray-300 text-gray-700 text-xs font-medium disabled:opacity-40"
                    >
                        Ajouter
                    </button>
                </div>
            )}
        </div>
    );
}

// ─── Run detail ───────────────────────────────────────────────────────────────

function RunDetail({
    run,
    clusters,
    busy,
    onAssign,
    onClose,
}: {
    run: ClientTypingRunDetail;
    clusters: ClientCluster[];
    busy: boolean;
    /** `clusterId: null` means "into a new cluster" — the parent asks for the name. */
    onAssign: (ids: string[], clusterId: string | null, profileLabel: string) => void;
    onClose: () => void;
}) {
    const [target, setTarget] = useState<Record<string, string>>({});

    return (
        <div className="bg-white border border-gray-900 rounded-xl p-4 mb-4">
            <div className="flex items-start justify-between gap-3 mb-3">
                <div>
                    <p className="text-sm font-semibold text-gray-900">
                        Analyse du {new Date(run.createdAt).toLocaleString("fr-FR")}
                    </p>
                    <p className="text-xs text-gray-500 mt-0.5">
                        {run.userEmail || "—"} · {run.establishmentCount} établissements · {run.profileCount} profils
                        {run.aiError ? ` · ${run.aiError}` : ""}
                        {run.model ? ` · ${run.model}` : ""}
                    </p>
                </div>
                <button onClick={onClose} className="text-gray-400 hover:text-gray-700" aria-label="Fermer">
                    <X className="w-4 h-4" />
                </button>
            </div>

            {run.summary && <p className="text-xs text-gray-700 mb-3 leading-relaxed">{run.summary}</p>}

            {/* Profiles — each one is a cluster candidate, which is the whole point of this view */}
            <div className="space-y-2 mb-4">
                {run.profiles.map((profile, i) => {
                    const ids = profile.memberIds ?? [];
                    return (
                        <div key={`${profile.label}-${i}`} className="border border-gray-200 rounded-lg p-3">
                            <div className="flex items-start justify-between gap-3">
                                <div className="min-w-0">
                                    <p className="text-xs font-semibold text-gray-900">
                                        {profile.pct}% · {profile.count} — {profile.label}
                                    </p>
                                    {profile.description && (
                                        <p className="text-[11px] text-gray-500 mt-0.5">{profile.description}</p>
                                    )}
                                    {profile.searchPrompt && (
                                        <p className="text-[11px] text-gray-400 mt-1 italic">« {profile.searchPrompt} »</p>
                                    )}
                                </div>
                                <div className="flex items-center gap-2 shrink-0">
                                    <select
                                        value={target[String(i)] ?? ""}
                                        onChange={e => setTarget(prev => ({ ...prev, [String(i)]: e.target.value }))}
                                        className="border border-gray-200 rounded-lg px-2 py-1 text-[11px] focus:outline-none focus:border-gray-400"
                                    >
                                        <option value="">Cluster…</option>
                                        {clusters.map(c => (
                                            <option key={c._id} value={c._id}>
                                                {c.name}
                                            </option>
                                        ))}
                                        <option value="__new">+ Nouveau cluster</option>
                                    </select>
                                    <button
                                        disabled={busy || ids.length === 0 || !target[String(i)]}
                                        onClick={() => {
                                            const choice = target[String(i)];
                                            onAssign(ids, choice === "__new" ? null : choice, profile.label);
                                            setTarget(prev => ({ ...prev, [String(i)]: "" }));
                                        }}
                                        className="px-2.5 py-1 rounded-lg bg-[#1A1A1A] text-white text-[11px] font-medium disabled:opacity-40"
                                    >
                                        Classer
                                    </button>
                                </div>
                            </div>
                            {profile.defining.length > 0 && (
                                <div className="flex flex-wrap gap-1 mt-2">
                                    {profile.defining.map(trait => (
                                        <span
                                            key={trait}
                                            className="px-2 py-0.5 rounded text-[10px] bg-gray-100 text-gray-600 border border-gray-200"
                                        >
                                            {trait}
                                        </span>
                                    ))}
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>

            <p className="text-[11px] font-medium text-gray-500 mb-1">Établissements analysés</p>
            <div className="flex flex-wrap gap-1">
                {run.establishments.map(e => (
                    <span
                        key={e.establishmentId}
                        className={`px-2 py-0.5 rounded text-[11px] border ${
                            e.deleted
                                ? "bg-red-50 text-red-600 border-red-100"
                                : "bg-gray-50 text-gray-600 border-gray-200"
                        }`}
                    >
                        {e.name}
                        {e.city ? ` · ${e.city}` : ""}
                    </span>
                ))}
            </div>
        </div>
    );
}

// ─── Page ─────────────────────────────────────────────────────────────────────

export default function ClientTypings() {
    const [tab, setTab] = useState<Tab>("runs");
    const [days, setDays] = useState<ClientTypingQuery["days"]>(30);
    const [status, setStatus] = useState<"all" | "ok" | "error">("all");
    const [search, setSearch] = useState("");
    const [debouncedSearch, setDebouncedSearch] = useState("");
    const [email, setEmail] = useState("");

    const [stats, setStats] = useState<ClientTypingStats | null>(null);
    const [byUser, setByUser] = useState<ClientTypingUserStat[]>([]);
    const [runs, setRuns] = useState<ClientTypingRun[]>([]);
    const [establishments, setEstablishments] = useState<AnalyzedEstablishment[]>([]);
    const [clusters, setClusters] = useState<ClientCluster[]>([]);
    const [openRun, setOpenRun] = useState<ClientTypingRunDetail | null>(null);
    const [selected, setSelected] = useState<string[]>([]);
    const [loading, setLoading] = useState(true);
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        const timer = setTimeout(() => setDebouncedSearch(search), 300);
        return () => clearTimeout(timer);
    }, [search]);

    const query: ClientTypingQuery = useMemo(
        () => ({ days, status, q: debouncedSearch || undefined, email: email || undefined }),
        [days, status, debouncedSearch, email]
    );

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const [statsRes, runsRes, establishmentsRes, clustersRes] = await Promise.all([
                fetchClientTypingStats(query),
                fetchClientTypingRuns(query, 1, 50),
                fetchAnalyzedEstablishments(query, 150),
                fetchClientClusters(query),
            ]);
            setStats(statsRes.stats);
            setByUser(statsRes.byUser);
            setRuns(runsRes.runs);
            setEstablishments(establishmentsRes);
            setClusters(clustersRes);
        } catch (err) {
            console.error("Failed to load client typings:", err);
        } finally {
            setLoading(false);
        }
    }, [query]);

    useEffect(() => { load(); }, [load]);

    /** Every cluster write goes through here, so the counts on screen are always re-read. */
    const runWrite = async (action: () => Promise<any>) => {
        setBusy(true);
        try {
            await action();
            setSelected([]);
            await load();
        } catch (err) {
            console.error("Cluster write failed:", err);
        } finally {
            setBusy(false);
        }
    };

    const openDetail = async (id: string) => {
        if (openRun?._id === id) return setOpenRun(null);
        try {
            setOpenRun(await fetchClientTypingRun(id));
        } catch (err) {
            console.error("Failed to load run:", err);
        }
    };

    const toggle = (id: string) =>
        setSelected(prev => (prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]));

    return (
        <div className="min-h-screen bg-[#F5F0EB]">
            <div className="mx-auto max-w-7xl px-4">
                <div className="mb-6">
                    <h1 className="text-2xl font-semibold text-gray-900">Typologies clients</h1>
                    <p className="text-sm text-gray-500 mt-0.5">
                        Les analyses lancées depuis « Intelligence clients », les établissements qu'elles regardent, et vos
                        clusters de clients
                    </p>
                </div>

                <BONavigation />

                {/* Stats */}
                <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
                    {[
                        { label: "Analyses", value: stats?.runs ?? 0 },
                        { label: "Utilisateurs", value: stats?.users ?? 0 },
                        { label: "Clients analysés", value: stats?.distinctEstablishments ?? 0 },
                        { label: "Profils générés", value: stats?.profiles ?? 0 },
                        { label: "Analyses en échec", value: stats?.failed ?? 0 },
                    ].map(card => (
                        <div key={card.label} className="bg-white border border-gray-200 rounded-xl p-4">
                            <p className="text-xs text-gray-500 mb-1">{card.label}</p>
                            <p className="text-2xl font-semibold text-gray-900">{card.value.toLocaleString()}</p>
                        </div>
                    ))}
                </div>

                {/* Filters */}
                <div className="bg-white border border-gray-200 rounded-xl p-4 mb-4 flex flex-wrap items-center gap-3">
                    <div className="flex gap-1">
                        {PERIODS.map(p => (
                            <button
                                key={String(p.value)}
                                onClick={() => setDays(p.value)}
                                className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                                    days === p.value ? "bg-[#1A1A1A] text-white" : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                                }`}
                            >
                                {p.label}
                            </button>
                        ))}
                    </div>
                    <div className="flex gap-1">
                        {(["all", "ok", "error"] as const).map(s => (
                            <button
                                key={s}
                                onClick={() => setStatus(s)}
                                className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                                    status === s ? "bg-[#1A1A1A] text-white" : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                                }`}
                            >
                                {{ all: "Toutes", ok: "Abouties", error: "En échec" }[s]}
                            </button>
                        ))}
                    </div>
                    {email && (
                        <button
                            onClick={() => setEmail("")}
                            className="flex items-center gap-1 px-2 py-1 rounded-lg bg-gray-100 text-xs text-gray-700"
                        >
                            {email} <X className="w-3 h-3" />
                        </button>
                    )}
                    <input
                        value={search}
                        onChange={e => setSearch(e.target.value)}
                        placeholder="Chercher un établissement ou un profil…"
                        className="ml-auto w-72 border border-gray-200 rounded-lg px-3 py-1.5 text-xs focus:outline-none focus:border-gray-400"
                    />
                </div>

                {/* Tabs */}
                <div className="flex gap-1 mb-4">
                    {([
                        ["runs", `Analyses (${runs.length})`],
                        ["establishments", `Clients analysés (${establishments.length})`],
                        ["clusters", `Clusters (${clusters.length})`],
                    ] as [Tab, string][]).map(([id, label]) => (
                        <button
                            key={id}
                            onClick={() => { setTab(id); setSelected([]); }}
                            className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                                tab === id ? "bg-white border border-gray-200 text-gray-900" : "text-gray-500 hover:text-gray-700"
                            }`}
                        >
                            {label}
                        </button>
                    ))}
                </div>

                {tab === "establishments" && (
                    <ClusterBar
                        label={`${selected.length} établissement${selected.length > 1 ? "s" : ""}`}
                        ids={selected}
                        clusters={clusters}
                        busy={busy}
                        onDone={runWrite}
                        onClear={() => setSelected([])}
                    />
                )}

                {openRun && tab === "runs" && (
                    <RunDetail
                        run={openRun}
                        clusters={clusters}
                        busy={busy}
                        onAssign={(ids, clusterId, label) => {
                            if (clusterId) {
                                runWrite(() => updateClientCluster(clusterId, { addEstablishments: ids }));
                                return;
                            }
                            const name = window.prompt("Nom du cluster", label);
                            if (name?.trim()) {
                                runWrite(() =>
                                    createClientCluster({
                                        name: name.trim(),
                                        establishmentIds: ids,
                                        origin: `Profil « ${label} »`,
                                    })
                                );
                            }
                        }}
                        onClose={() => setOpenRun(null)}
                    />
                )}

                {loading ? (
                    <div className="bg-white border border-gray-200 rounded-xl py-16 text-center text-sm text-gray-400">
                        Chargement…
                    </div>
                ) : tab === "runs" ? (
                    <RunsTable runs={runs} openId={openRun?._id ?? null} onOpen={openDetail} onPickUser={setEmail} />
                ) : tab === "establishments" ? (
                    <EstablishmentsTable
                        rows={establishments}
                        selected={selected}
                        onToggle={toggle}
                        onSelectAll={() =>
                            setSelected(
                                selected.length === establishments.length
                                    ? []
                                    : establishments.map(e => e.establishmentId)
                            )
                        }
                    />
                ) : (
                    <ClustersView
                        clusters={clusters}
                        busy={busy}
                        onRename={(id, name) => runWrite(() => updateClientCluster(id, { name }))}
                        onRemove={(id, establishmentId) =>
                            runWrite(() => updateClientCluster(id, { removeEstablishments: [establishmentId] }))
                        }
                        onDelete={id => runWrite(() => deleteClientCluster(id))}
                    />
                )}

                {/* Who runs the analyses — the other half of "which user looked at what" */}
                {byUser.length > 0 && tab === "runs" && (
                    <div className="bg-white border border-gray-200 rounded-xl p-4 mt-4">
                        <p className="text-xs font-medium text-gray-500 mb-3 flex items-center gap-1.5">
                            <Users className="w-3.5 h-3.5" /> Par utilisateur
                        </p>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-2">
                            {byUser.map(u => (
                                <button
                                    key={u.email}
                                    onClick={() => setEmail(u.email)}
                                    className="flex items-center justify-between gap-3 text-xs hover:bg-gray-50 rounded px-1 py-0.5"
                                >
                                    <span className="text-gray-700 truncate">{u.email || "—"}</span>
                                    <span className="text-gray-400 shrink-0">
                                        {u.runs} analyse{u.runs > 1 ? "s" : ""} · {u.establishments} clients ·{" "}
                                        {formatDate(u.lastRunAt)}
                                    </span>
                                </button>
                            ))}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}

// ─── Runs table ───────────────────────────────────────────────────────────────

function RunsTable({
    runs,
    openId,
    onOpen,
    onPickUser,
}: {
    runs: ClientTypingRun[];
    openId: string | null;
    onOpen: (id: string) => void;
    onPickUser: (email: string) => void;
}) {
    if (runs.length === 0) {
        return (
            <div className="bg-white border border-gray-200 rounded-xl py-16 text-center text-sm text-gray-400">
                Aucune analyse sur la période.
            </div>
        );
    }

    return (
        <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
            <table className="w-full">
                <thead>
                    <tr className="border-b border-gray-100">
                        <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 w-44">Utilisateur</th>
                        <th className="text-left px-4 py-3 text-xs font-medium text-gray-500">Profils</th>
                        <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 w-24">Clients</th>
                        <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 w-28">Durée</th>
                        <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 w-28">Quand</th>
                    </tr>
                </thead>
                <tbody>
                    {runs.map(run => (
                        <tr
                            key={run._id}
                            onClick={() => onOpen(run._id)}
                            className={`border-b border-gray-50 hover:bg-[#F9F6F3] cursor-pointer ${
                                openId === run._id ? "bg-[#F5F0EB]" : ""
                            }`}
                        >
                            <td className="px-4 py-3">
                                <button
                                    onClick={e => { e.stopPropagation(); onPickUser(run.userEmail); }}
                                    className="text-xs text-gray-900 hover:underline truncate max-w-[10rem] block text-left"
                                >
                                    {run.userEmail || "—"}
                                </button>
                                {run.userName && <p className="text-[11px] text-gray-400">{run.userName}</p>}
                            </td>
                            <td className="px-4 py-3">
                                {run.aiError ? (
                                    <span className="px-2 py-0.5 rounded text-[11px] font-medium border bg-red-50 text-red-700 border-red-100">
                                        {run.aiError}
                                    </span>
                                ) : (
                                    <div className="flex flex-wrap gap-1">
                                        {run.profiles.map((p, i) => (
                                            <span
                                                key={`${p.label}-${i}`}
                                                className="px-2 py-0.5 rounded text-[11px] bg-gray-100 text-gray-700 border border-gray-200"
                                            >
                                                {p.label} · {p.pct}%
                                            </span>
                                        ))}
                                    </div>
                                )}
                            </td>
                            <td className="px-4 py-3 text-xs text-gray-700">{run.establishmentCount}</td>
                            <td className="px-4 py-3 text-xs text-gray-500">
                                {run.durationMs ? `${(run.durationMs / 1000).toFixed(1)}s` : "—"}
                            </td>
                            <td className="px-4 py-3 text-xs text-gray-500">{formatDate(run.createdAt)}</td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
}

// ─── Analysed establishments ──────────────────────────────────────────────────

function EstablishmentsTable({
    rows,
    selected,
    onToggle,
    onSelectAll,
}: {
    rows: AnalyzedEstablishment[];
    selected: string[];
    onToggle: (id: string) => void;
    onSelectAll: () => void;
}) {
    if (rows.length === 0) {
        return (
            <div className="bg-white border border-gray-200 rounded-xl py-16 text-center text-sm text-gray-400">
                Aucun établissement analysé sur la période.
            </div>
        );
    }

    const maxRuns = rows[0]?.runs || 1;

    return (
        <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
            <table className="w-full">
                <thead>
                    <tr className="border-b border-gray-100">
                        <th className="px-4 py-3 w-8">
                            <input
                                type="checkbox"
                                checked={selected.length === rows.length && rows.length > 0}
                                onChange={onSelectAll}
                                className="rounded border-gray-300"
                            />
                        </th>
                        <th className="text-left px-4 py-3 text-xs font-medium text-gray-500">Établissement</th>
                        <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 w-48">Clusters</th>
                        <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 w-36">Analyses</th>
                        <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 w-28">Dernière</th>
                    </tr>
                </thead>
                <tbody>
                    {rows.map(row => (
                        <tr
                            key={row.establishmentId}
                            className={`border-b border-gray-50 hover:bg-[#F9F6F3] ${
                                selected.includes(row.establishmentId) ? "bg-[#F5F0EB]" : ""
                            }`}
                        >
                            <td className="px-4 py-3">
                                <input
                                    type="checkbox"
                                    checked={selected.includes(row.establishmentId)}
                                    onChange={() => onToggle(row.establishmentId)}
                                    className="rounded border-gray-300"
                                />
                            </td>
                            <td className="px-4 py-3">
                                <a
                                    href={`/restaurants/${row.establishmentId}`}
                                    target="_blank"
                                    rel="noreferrer"
                                    className="text-xs text-gray-900 hover:underline flex items-center gap-1.5"
                                >
                                    <Building2 className="w-3 h-3 text-gray-400" />
                                    {row.name}
                                </a>
                                <p className="text-[11px] text-gray-400 mt-0.5">
                                    {[row.city, ...row.categories.slice(0, 2), ...row.subCategories.slice(0, 2)]
                                        .filter(Boolean)
                                        .join(" · ") || "—"}
                                </p>
                                <p className="text-[11px] text-gray-400">{row.users.join(" · ")}</p>
                            </td>
                            <td className="px-4 py-3">
                                {row.clusters.length > 0 ? (
                                    <div className="flex flex-wrap gap-1">
                                        {row.clusters.map(c => (
                                            <span
                                                key={c._id}
                                                className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[11px] font-medium bg-gray-100 text-gray-700 border border-gray-200"
                                            >
                                                <Layers className="w-3 h-3" />
                                                {c.name}
                                            </span>
                                        ))}
                                    </div>
                                ) : (
                                    <span className="text-[11px] text-gray-300">—</span>
                                )}
                            </td>
                            <td className="px-4 py-3">
                                <div className="flex items-center gap-2">
                                    <div className="flex-1 h-1.5 bg-gray-100 rounded-full overflow-hidden">
                                        <div
                                            className="h-full bg-[#1A1A1A] rounded-full"
                                            style={{ width: `${(row.runs / maxRuns) * 100}%` }}
                                        />
                                    </div>
                                    <span className="text-xs font-medium text-gray-900 w-8 text-right">{row.runs}x</span>
                                </div>
                            </td>
                            <td className="px-4 py-3 text-xs text-gray-500">{formatDate(row.lastAnalyzedAt)}</td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
}

// ─── Clusters ─────────────────────────────────────────────────────────────────

function ClustersView({
    clusters,
    busy,
    onRename,
    onRemove,
    onDelete,
}: {
    clusters: ClientCluster[];
    busy: boolean;
    onRename: (id: string, name: string) => void;
    onRemove: (id: string, establishmentId: string) => void;
    onDelete: (id: string) => void;
}) {
    if (clusters.length === 0) {
        return (
            <div className="bg-white border border-gray-200 rounded-xl py-16 text-center text-sm text-gray-400">
                Aucun cluster. Classez un profil depuis une analyse, ou sélectionnez des établissements dans l'onglet
                précédent.
            </div>
        );
    }

    const maxMembers = Math.max(...clusters.map(c => c.memberCount), 1);

    return (
        <div className="space-y-3">
            {clusters.map(cluster => (
                <div key={cluster._id} className="bg-white border border-gray-200 rounded-xl p-4">
                    <div className="flex items-start gap-3 mb-3">
                        <div className="flex-1 min-w-0">
                            <h3 className="text-sm font-semibold text-gray-900 flex items-center gap-1.5">
                                <Sparkles className="w-3.5 h-3.5 text-gray-400" />
                                {cluster.name}
                            </h3>
                            <p className="text-xs text-gray-500 mt-0.5">
                                {cluster.memberCount} client{cluster.memberCount > 1 ? "s" : ""} · {cluster.runs} analyse
                                {cluster.runs > 1 ? "s" : ""} · {cluster.users} utilisateur{cluster.users > 1 ? "s" : ""}
                                {cluster.origin ? ` · ${cluster.origin}` : ""}
                            </p>
                        </div>
                        <span className="text-[11px] text-gray-400 shrink-0">{formatDate(cluster.lastAnalyzedAt)}</span>
                        <button
                            disabled={busy}
                            onClick={() => {
                                const name = window.prompt("Nouveau nom du cluster", cluster.name);
                                if (name?.trim()) onRename(cluster._id, name.trim());
                            }}
                            className="text-[11px] text-gray-500 hover:text-gray-800 disabled:opacity-40"
                        >
                            Renommer
                        </button>
                        <button
                            disabled={busy}
                            onClick={() => {
                                if (window.confirm(`Supprimer le cluster "${cluster.name}" ? Les analyses sont conservées.`))
                                    onDelete(cluster._id);
                            }}
                            className="text-gray-400 hover:text-red-600 disabled:opacity-40"
                            aria-label="Supprimer le cluster"
                        >
                            <Trash2 className="w-3.5 h-3.5" />
                        </button>
                    </div>

                    <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden mb-3">
                        <div
                            className="h-full bg-[#1A1A1A] rounded-full"
                            style={{ width: `${(cluster.memberCount / maxMembers) * 100}%` }}
                        />
                    </div>

                    <div className="flex flex-wrap gap-1">
                        {cluster.members.map(member => (
                            <span
                                key={member.establishmentId}
                                className="group inline-flex items-center gap-1 px-2 py-0.5 rounded text-[11px] bg-gray-50 text-gray-600 border border-gray-200"
                            >
                                {member.name}
                                {member.city ? ` · ${member.city}` : ""}
                                <span className="text-gray-400">· {member.runs}x</span>
                                <button
                                    disabled={busy}
                                    onClick={() => onRemove(cluster._id, member.establishmentId)}
                                    className="text-gray-300 hover:text-red-600 disabled:opacity-40"
                                    aria-label={`Retirer ${member.name}`}
                                >
                                    <X className="w-3 h-3" />
                                </button>
                            </span>
                        ))}
                    </div>
                </div>
            ))}
        </div>
    );
}
